<template>
<main class="container-1">

    <div class="container-header">
        <Breadcrumb
        v-bind:links="routesBread"
        >
        </Breadcrumb>
        <TabNav :links="routesNav" />
    </div>

    <div class="container-body">

        <p v-if="message" class="sym-message">{{ message }}</p>

        <p v-if="!checked">Cargando información del servicio ArcGIS...</p>

        <p v-else-if="!metaLoaded" class="sym-blocked">
            No fue posible consultar el servicio ArcGIS de esta capa.
        </p>

        <p v-else-if="!isSupportedGeometry" class="sym-blocked">
            Sólo se pueden definir simbologías para capas de puntos, líneas y polígonos.
        </p>

        <p v-else-if="isMapServer && !supportsDynamic" class="sym-blocked">
            El servicio ArcGIS de esta capa no soporta simbología personalizada
            (<code>supportsDynamicLayers: false</code>).
        </p>

        <template v-else-if="mode === 'list'">
            <div class="container-action-left" style="justify-content: end">
                <button class="btn btn-primary" @click="newSymbology">Nueva simbología</button>
            </div>

            <p v-if="!symbologies.length" class="sym-empty">Esta capa no tiene simbologías guardadas.</p>

            <table v-else class="sym-table">
                <thead>
                    <tr><th>Nombre</th><th>Tipo</th><th>Acciones</th></tr>
                </thead>
                <tbody>
                    <tr v-for="item in symbologies" :key="item.id">
                        <td>{{ item.name }}</td>
                        <td>{{ rendererTypeLabel(item.renderer) }}</td>
                        <td class="sym-table__actions">
                            <button class="btn btn-secondary" @click="editSymbology(item)">Editar</button>
                            <button class="btn btn-danger" @click="confirmDelete(item)">Eliminar</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>

        <template v-else>
            <div class="sym-editor">
                <div class="sym-editor__form">
                    <label for="sym-name">Nombre</label>
                    <input id="sym-name" type="text" v-model="draftName" placeholder="Nombre de la simbología">

                    <label for="sym-type">Tipo de simbología</label>
                    <select id="sym-type" :value="draft.type" @change="setRendererType($event.target.value)">
                        <option value="simple">Símbolo único</option>
                        <option value="uniqueValue">Por valores únicos</option>
                        <option value="classBreaks">Por rangos</option>
                    </select>

                    <template v-if="draft.type === 'simple'">
                        <h3 class="sym-editor__subtitle">Símbolo</h3>
                        <component :is="symbolEditor" :symbol="draft.symbol" @input="draft.symbol = $event" @error="message = $event" />
                    </template>

                    <template v-else-if="draft.type === 'uniqueValue'">
                        <label for="sym-field1">Campo categórico</label>
                        <select id="sym-field1" :value="draft.field1" @change="onUniqueFieldChange($event.target.value)">
                            <option value="">Seleccione un campo</option>
                            <option v-for="opt in categoricalOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                        <p v-if="arcgisFields && !categoricalFields.length" class="sym-hint">
                            No hay campos categóricos disponibles en el servicio: no es posible definir valores únicos.
                        </p>
                        <p v-else-if="!arcgisFields" class="sym-hint">
                            No se pudo determinar el tipo de los campos del servicio: se listan todos.
                        </p>

                        <label for="sym-palette-unique">Paleta de colores</label>
                        <div class="sym-palette">
                            <select id="sym-palette-unique" :value="paletteId" @change="setPalette($event.target.value)">
                                <!-- Sólo mientras ES el estado actual: volver a elegirla no
                                     devuelve los colores anteriores, no hay deshacer. -->
                                <option v-if="!paletteId" value="">Sin paleta (colores actuales)</option>
                                <option v-for="palette in paletteOptions" :key="palette.id" :value="palette.id">{{ palette.name }}</option>
                            </select>
                            <span class="sym-palette__strip">
                                <span v-for="hex in paletteColors || []" :key="hex" :style="{ background: hex }"></span>
                            </span>
                            <!-- Un <select> nativo no emite `change` al re-elegir la
                                 opción ya seleccionada, y "Sin paleta" desaparece del
                                 dropdown en cuanto hay paleta: sin este botón no hay
                                 forma de aplicar la paleta que el selector ya muestra. -->
                            <button v-if="paletteId" class="btn btn-secondary" @click="setPalette(paletteId)">Aplicar paleta</button>
                        </div>
                        <p v-if="paletteRepeatsHint" class="sym-hint">
                            Hay más valores que colores en la paleta: los colores se repiten cada {{ paletteColors.length }}.
                        </p>

                        <div v-for="(info, index) in draft.uniqueValueInfos" :key="index" class="sym-class">
                            <div class="sym-class__head">
                                <input type="text" v-model="info.value" placeholder="Valor">
                                <input type="text" v-model="info.label" placeholder="Etiqueta">
                                <button class="btn btn-secondary" @click="draft.uniqueValueInfos.splice(index, 1)">Quitar</button>
                            </div>
                            <component :is="symbolEditor" :symbol="info.symbol" @input="$set(info, 'symbol', $event)" @error="message = $event" />
                        </div>

                        <button class="btn btn-secondary" @click="addUniqueValue">Agregar valor</button>

                        <h3 class="sym-editor__subtitle">Símbolo por defecto</h3>
                        <component :is="symbolEditor" :symbol="draft.defaultSymbol" @input="draft.defaultSymbol = $event" @error="message = $event" />
                    </template>

                    <template v-else>
                        <label for="sym-field">Campo numérico</label>
                        <select id="sym-field" :value="draft.field" @change="onClassFieldChange($event.target.value)">
                            <option value="">Seleccione un campo</option>
                            <option v-for="opt in numericOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                        <p v-if="!numericFields.length" class="sym-hint">
                            No hay campos numéricos disponibles en el servicio: no es posible definir rangos.
                        </p>

                        <label for="sym-method">Método de corte</label>
                        <select id="sym-method" :value="classificationMethod" @change="onMethodChange($event.target.value)">
                            <option v-for="item in classificationMethods" :key="item.value" :value="item.value">{{ item.label }}</option>
                        </select>

                        <template v-if="classificationMethod !== 'manual'">
                            <template v-if="isStdDevMethod">
                                <label for="sym-stddev">Intervalo de desviación</label>
                                <select id="sym-stddev" :value="standardDeviationInterval" @change="onStdDevIntervalChange($event.target.value)">
                                    <option v-for="value in stdDevIntervals" :key="value" :value="value">{{ value }}</option>
                                </select>
                            </template>
                            <template v-else>
                                <label for="sym-breakcount">Número de clases</label>
                                <!-- @change y no @input: con @input cada tecla sería una petición. -->
                                <input id="sym-breakcount" type="number" min="2" v-model.number="breakCount"
                                       @change="applyClassification">
                            </template>
                        </template>

                        <label for="sym-minvalue">Valor mínimo</label>
                        <input id="sym-minvalue" type="number" :value="draft.minValue"
                               @input="$set(draft, 'minValue', $event.target.value); regenerateLabels()">
                        <p class="sym-hint">Vacío = sin piso: se dibujan todas las features bajo el primer rango.</p>

                        <label for="sym-decimals">Decimales en las etiquetas</label>
                        <select id="sym-decimals" :value="labelDecimals" @change="onDecimalsChange($event.target.value)">
                            <option v-for="value in [0, 1, 2, 3, 4]" :key="value" :value="value">{{ value }}</option>
                        </select>
                        <p class="sym-hint">Las etiquetas de los rangos se generan automáticamente: al cambiar los decimales o los cortes se reescriben.</p>

                        <label for="sym-palette-class">Paleta de colores</label>
                        <div class="sym-palette">
                            <select id="sym-palette-class" :value="paletteId" @change="setPalette($event.target.value)">
                                <option v-if="!paletteId" value="">Sin paleta (colores actuales)</option>
                                <option v-for="palette in paletteOptions" :key="palette.id" :value="palette.id">{{ palette.name }}</option>
                            </select>
                            <span class="sym-palette__strip">
                                <span v-for="hex in paletteColors || []" :key="hex" :style="{ background: hex }"></span>
                            </span>
                            <button v-if="paletteId" class="btn btn-secondary" @click="setPalette(paletteId)">Aplicar paleta</button>
                        </div>

                        <div v-for="(info, index) in draft.classBreakInfos" :key="index" class="sym-class">
                            <div class="sym-class__head">
                                <input type="number" v-model.number="info.classMaxValue" placeholder="Máximo"
                                       @change="setPalette(paletteId); regenerateLabels()">
                                <input type="text" v-model="info.label" placeholder="Etiqueta">
                                <button class="btn btn-secondary" @click="removeClassBreak(index)">Quitar</button>
                            </div>
                            <component :is="symbolEditor" :symbol="info.symbol" @input="$set(info, 'symbol', $event)" @error="message = $event" />
                        </div>

                        <button class="btn btn-secondary" @click="addClassBreak">Agregar rango</button>
                    </template>
                </div>

                <aside class="sym-editor__preview">
                    <h3 class="sym-editor__subtitle">Vista previa</h3>
                    <SymbologySwatches :renderer="draft" />

                    <template v-if="draft.type === 'classBreaks'">
                        <button class="btn btn-secondary" @click="toggleHistogram">
                            {{ histogramOpen ? 'Ocultar distribución' : 'Ver distribución' }}
                        </button>
                        <div v-if="histogramOpen">
                            <p v-if="!draft.field" class="sym-hint">Seleccione un campo para ver la distribución.</p>
                            <p v-else-if="histogramLoading" class="sym-hint">Calculando la distribución…</p>
                            <p v-else-if="histogramError" class="sym-hint">{{ histogramError }}</p>
                            <template v-else-if="histogramBins.length">
                                <!-- Título como markup y no como opción `title` de ApexCharts: el
                                     contenedor del gráfico mide 240px con padding cero a propósito
                                     (el padding se resta del alto que mide getDimensions), y un
                                     título dentro del canvas se comería el alto útil. -->
                                <h4 class="sym-histogram-title">Distribución de {{ draft.field }}</h4>
                                <SymbologyHistogram :bins="histogramBins"
                                                    :class-break-infos="draft.classBreakInfos || []"
                                                    :min-value="draft.minValue"
                                                    :decimals="labelDecimals"
                                                    :field="draft.field" />
                                <p class="sym-hint">
                                    El gráfico representa la <strong>densidad</strong> de las observaciones,
                                    no su frecuencia.
                                    <FieldHelp label="la densidad"
                                               align="right"
                                               text="La densidad es la proporción de las observaciones visibles por unidad de valor: no es un porcentaje y no va a sumar 1 si se suman los valores de las barras tal cual se leen, porque cada una hay que ponderarla por su ancho. Como la cantidad de intervalos se calcula a partir del número de observaciones, la altura de las barras cambiaría al cambiar de campo si se graficara la frecuencia; la densidad mantiene el significado del eje Y comparable entre campos y entre tamaños de conjunto." />
                                </p>
                                <ul v-if="histogramRangeInfoItems.length" class="sym-hint">
                                    <li v-for="(item, i) in histogramRangeInfoItems" :key="i">{{ item }}</li>
                                </ul>
                            </template>
                            <!-- Fuera de la cadena a propósito: convive con el gráfico. -->
                            <p v-if="histogramPartial" class="sym-hint">{{ histogramPartial }}</p>
                        </div>
                    </template>
                </aside>
            </div>

            <div class="container-action-left" style="justify-content: end; margin-top: 20px">
                <button class="btn btn-secondary" @click="mode = 'list'; message = ''">Cancelar</button>
                <button class="btn btn-primary" @click="save">Guardar</button>
            </div>
        </template>

    </div>

</main>
</template>

<script>
// @ is an alias to /src
import Breadcrumb from '@/components/Breadcrumb.vue'
import TabNav from '@/components/TabNav.vue'
import SymbologySwatches from '@/components/symbology/SymbologySwatches.vue'
import SymbologySymbolEditor from '@/components/symbology/SymbologySymbolEditor.vue'
import SymbologyLineEditor from '@/components/symbology/SymbologyLineEditor.vue'
import SymbologyPolygonEditor from '@/components/symbology/SymbologyPolygonEditor.vue'
import SymbologyHistogram from '@/components/symbology/SymbologyHistogram.vue'
import FieldHelp from '@/components/FieldHelp.vue'

import { UrlHandler } from '@/services/utils/UrlHandler.js'
import { restLayer } from '@/components/mixin/restLayer.js'
import { restSymbology } from '@/components/mixin/restSymbology.js'
import {
    defaultSymbolFor, rendererSwatches, LINE_STYLE_VALUES,
    QUALITATIVE_PALETTES, SEQUENTIAL_PALETTES,
    qualitativeColor, sequentialColor, applyColor, rgbToHex,
    formatBreakLabels, inferDecimals, inferDecimalsFromLabel
} from '@/services/layers/symbology'

const RENDERER_LABELS = {
    'simple': 'Símbolo único',
    'uniqueValue': 'Por valores únicos',
    'classBreaks': 'Por rangos',
}

//El valor 'manual' no tiene classificationMethod: es la ausencia de cálculo, no
//un método Esri. `stdDev` marca al único que toma standardDeviationInterval en
//vez de breakCount (decisión 5) — el número de clases lo decide el servicio.
const CLASSIFICATION_METHODS = [
    { value: 'manual',              label: 'Manual',                        esri: '' },
    { value: 'quantile',            label: 'Cuantil',                       esri: 'esriClassifyQuantile' },
    { value: 'naturalBreaks',       label: 'Rupturas Naturales (Jenks)',    esri: 'esriClassifyNaturalBreaks' },
    { value: 'equalInterval',       label: 'Intervalos Iguales',            esri: 'esriClassifyEqualInterval' },
    { value: 'standardDeviation',   label: 'Desviación Estándar',           esri: 'esriClassifyStandardDeviation', stdDev: true },
    { value: 'geometricalInterval', label: 'Intervalos Geométricos',        esri: 'esriClassifyGeometricalInterval' },
]

const STD_DEV_INTERVALS = [1, 0.5, 0.33, 0.25]

//El visor nunca consulta geometryType: la invariante es que sólo las
//geometrías soportadas pueden tener simbologías guardadas.
const SUPPORTED_GEOMETRIES = ['esriGeometryPoint', 'esriGeometryPolyline', 'esriGeometryPolygon']

const EDITORS = {
    esriGeometryPoint: 'SymbologySymbolEditor',
    esriGeometryPolyline: 'SymbologyLineEditor',
    esriGeometryPolygon: 'SymbologyPolygonEditor',
}

//esriSFSNull y esriSLSNull significan "sin relleno" y "sin trazo": forzar el
//estilo a Solid sin tocar el color deja la semilla opaca tapando el mapa base
//(el defecto que D23 arregló en el traductor). La opacidad 0 es el mecanismo
//único de D22 para lo invisible, y vale igual en OL y en dynamicLayers.
function transparentize(symbol) {
    if (!Array.isArray(symbol.color) || symbol.color.length < 3) symbol.color = [0, 0, 0, 0]
    else symbol.color[3] = 0
}

//Recorre las mismas ranuras que rendererSwatches(), más el outline anidado y el
//defaultSymbol de classBreaks que rendererSwatches() no expone (viajan verbatim
//a MapServer si no se normalizan acá). El outline se recorre para cualquier
//símbolo: el de un esriSMS también es un esriSLS y el editor de puntos no
//expone su patrón, así que nadie más puede corregirlo.
function normalizeSymbol(symbol) {
    if (!symbol) return
    if (symbol.type === 'esriSFS') {
        if (symbol.style === 'esriSFSNull') transparentize(symbol)
        symbol.style = 'esriSFSSolid'
    } else if (symbol.type === 'esriSLS' && LINE_STYLE_VALUES.indexOf(symbol.style) === -1) {
        if (symbol.style === 'esriSLSNull') transparentize(symbol)
        symbol.style = 'esriSLSSolid'
    }
    normalizeSymbol(symbol.outline)
}

//Todas las ranuras de símbolo del renderer: las de rendererSwatches() más el
//defaultSymbol de classBreaks, que ese helper no expone (viaja verbatim a
//MapServer si nadie lo mira). Un solo recorrido para normalizar y para validar
//la semilla.
function rendererSymbols(renderer) {
    var symbols = rendererSwatches(renderer).map(function(row){ return row.symbol })
    if (renderer.type === 'classBreaks' && renderer.defaultSymbol) symbols.push(renderer.defaultSymbol)
    return symbols
}

function normalizeSymbolStyles(renderer) {
    rendererSymbols(renderer).forEach(function(symbol){ normalizeSymbol(symbol) })
}

//El campo ya guardado se antepone marcado: un <select> cuyo :value no matchea
//ninguna <option> deja selectedIndex = -1 y se ve VACÍO, aunque el draft sí
//tenga campo. El value sigue siendo el nombre crudo; la marca es sólo texto.
function fieldOptions(names, selected, mark) {
    var options = names.map(function(name){ return { value: name, label: name } })
    if (selected && names.indexOf(selected) === -1) {
        options.unshift({ value: selected, label: selected + ' (' + mark + ')' })
    }
    return options
}

//Catálogo visible por rama (decisión 6): las categorías no tienen orden y piden
//tonos bien separados; los rangos sí lo tienen y piden una rampa donde "más
//oscuro = más".
function palettesFor(type) {
    if (type === 'uniqueValue') return QUALITATIVE_PALETTES
    if (type === 'classBreaks') return SEQUENTIAL_PALETTES
    return []
}

function defaultPaletteFor(type) {
    var palettes = palettesFor(type)
    return palettes.length ? palettes[0].id : ''
}

export default{
    name: 'LayersSymbologies',
    mixins: [restLayer, restSymbology],
    components:{
        Breadcrumb,
        TabNav,
        SymbologySwatches,
        SymbologySymbolEditor,
        SymbologyLineEditor,
        SymbologyPolygonEditor,
        SymbologyHistogram,
        FieldHelp
    },
    data(){
        return{
            //restLayer nunca declara `message` pero todos sus .catch lo asignan.
            message: '',
            //null mientras no llega la metadata del servicio: recién ahí se
            //puede decidir si la capa admite simbologías.
            checked: false,
            //Distingue "el servicio no respondió" de "la capa no es de puntos":
            //sin esto un servicio caído se reporta como capa no soportada.
            metaLoaded: false,
            geometryType: '',
            isMapServer: false,
            supportsDynamic: false,
            defaultRenderer: null,
            arcgisFields: null,
            //Techo del servicio para /query: sin él no se puede saber si la lista de valores
            //distintos vino cortada.
            maxRecordCount: 0,
            mode: 'list',
            editingId: null,
            draftName: '',
            draft: null,
            //Hermano de `draft`, NUNCA dentro: normalizedDraft() clona el draft
            //entero sin whitelist y el renderer viaja verbatim a body.renderer, al
            //bson.M sin schema del backend y de ahí a drawingInfo.renderer del
            ///export de MapServer. Una llave fuera del vocabulario Esri ahí arriesga
            //un 400 — ese camino ya costó un "Missing 'source' in dynamic layer
            //definition" (symbology.js:272-275). '' = sin paleta activa.
            paletteId: '',
            //Hermanos de `draft` por la misma razón que paletteId: el método de
            //clasificación es parámetro de /generateRenderer, no vocabulario de un
            //renderer Esri, y el draft viaja verbatim al bson.M y de ahí a
            //drawingInfo.renderer (decisión 10). No se persiste.
            classificationMethod: 'manual',
            breakCount: 5,
            standardDeviationInterval: 1,
            //Cuatro controles disparan applyClassification y ninguno espera al anterior:
            //sin token gana la respuesta que llega última, aunque sea la petición vieja.
            classificationToken: 0,
            //Hermano de `draft` por la misma razón que paletteId: los decimales no son
            //vocabulario de un renderer Esri. NO se persisten como dato — quedan horneados
            //en los strings de `label` (decisión 4). Cero llaves nuevas, cero backend.
            labelDecimals: 2,
            //La inferencia existe para acertar el defecto, no para ganarle al usuario:
            //sin esto, elegir 3 decimales y después subir el número de clases dispararía
            //la reclasificación, la inferencia volvería a 0 o 2 y el control parecería
            //no tomar.
            decimalsTouched: false,
            //Espejo de classificationToken: veinte peticiones en paralelo son la superficie
            //de carrera más ancha del editor.
            histogramToken: 0,
            histogramOpen: false,
            histogramLoading: false,
            //'' = no hay error.
            histogramError: '',
            //Índices de bins cuyo conteo no se pudo obtener. Separado de histogramError a
            //propósito: ese oculta el gráfico (cayeron las 20), este convive con él.
            histogramFailed: [],
            //Cacheado POR NOMBRE DE CAMPO mientras dure la edición: cambiar de método, de
            //número de clases, de intervalo o de decimales no dispara ninguna petición
            //nueva, sólo mueve los colores sobre las barras ya cargadas.
            histogramField: '',
            histogramBins: [],
            //Estadística de TODAS las observaciones del campo: { min, max, count, avg, stddev }.
            //Es la fuente del mínimo/máximo reales que el texto informa aunque queden fuera
            //del rango visible, y del número de observaciones que decide la cantidad de bins.
            histogramStats: null,
            //Rango efectivamente dibujado { from, to }. Igual a min/max cuando no hay recorte.
            histogramVisible: null,
            //Primer valor recortado de cada cola, o null si esa cola no se recortó. `low` es
            //el MAYOR valor bajo el rango visible y `high` el MENOR valor sobre él: el primero
            //que se encuentra al salir de la vista, no el extremo de los datos (ese es
            //histogramStats.min/max). null también cuando la petición de la cola falló:
            //el gráfico se queda, el texto de esa cola se omite.
            histogramClipped: null,
        }
    },
    computed: {
        isSupportedGeometry: function(){
            return SUPPORTED_GEOMETRIES.indexOf(this.geometryType) !== -1
        },
        symbolEditor: function(){
            return EDITORS[this.geometryType] || 'SymbologySymbolEditor'
        },
        routesNav: function(){
          return [
                {name:'Editar capa' , path: '/capas/' + this.$route.params.idLayer + '/editar-capa'},
                {name:'Asignar mapas' , path: '/capas/' + this.$route.params.idLayer + '/asignar-mapas'},
                {name:'Simbologías' , path: '/capas/' + this.$route.params.idLayer + '/simbologias'},
                {name:'Historial de cambios' , path: '/capas/' + this.$route.params.idLayer + '/historial-de-cambios'},
                {name:'Eliminar capa' , path: '/capas/' + this.$route.params.idLayer + '/eliminar-capa'},
              ]
        },
        routesBread: function(){
          return [
              {name:'Capas', path: '/capas' },
              {name: this.layer.name, path: '/capas/' + this.$route.params.idLayer},
              {name: 'Simbologías'}
              ]
        },
        //`Campo` del backend no guarda el tipo del campo: los tipos sólo existen
        //en el documento vivo de ArcGIS. Sin tipo no se puede impedir un
        //classBreaks sobre un campo de texto, que no dibujaría nada en silencio.
        fieldNames: function(){
            return this.arcgisFields
                ? this.arcgisFields.map(function(field){ return field.name })
                : (this.layer.campos || []).map(function(campo){ return campo.name })
        },
        numericFields: function(){
            //Sin metadata del servicio no se ofrecen rangos.
            return this.arcgisFields
                ? this.arcgisFields
                    .filter(function(field){ return /(int|double|single|float)/i.test(field.type || '') })
                    .map(function(field){ return field.name })
                : []
        },
        //Asimétrico con numericFields a propósito: un uniqueValue sobre el campo
        //equivocado dibuja mal, no deja de dibujar. Sin metadata se listan todos
        //los campos en vez de bloquear la rama.
        categoricalFields: function(){
            if (!this.arcgisFields) return this.fieldNames
            return this.arcgisFields
                .filter(function(field){
                    var type = field.type || ''
                    //OID es identificador único: una categoría por feature. Fuera
                    //aunque un servicio lo publique con otro nombre que sí contenga
                    //una raíz de entero.
                    if (/oid|objectid/i.test(type)) return false
                    return /(string|guid|globalid|smallinteger|integer|biginteger)/i.test(type)
                })
                .map(function(field){ return field.name })
        },
        categoricalOptions: function(){
            return fieldOptions(this.categoricalFields, this.draft.field1, 'no categórico')
        },
        numericOptions: function(){
            return fieldOptions(this.numericFields, this.draft.field, 'no numérico')
        },
        paletteOptions: function(){
            return palettesFor(this.draft.type)
        },
        classificationMethods: function(){ return CLASSIFICATION_METHODS },
        stdDevIntervals: function(){ return STD_DEV_INTERVALS },
        //null = sin paleta activa: los cuatro puntos de nacimiento no pintan y la
        //clase nace con defaultSymbolFor() como hoy.
        paletteColors: function(){
            var id = this.paletteId
            var palette = this.paletteOptions.find(function(item){ return item.id === id })
            return palette ? palette.colors : null
        },
        paletteRepeatsHint: function(){
            var colors = this.paletteColors
            return !!colors && this.draft.type === 'uniqueValue' &&
                   this.draft.uniqueValueInfos.length > colors.length
        },
        isStdDevMethod: function(){
            var value = this.classificationMethod
            var method = CLASSIFICATION_METHODS.find(function(item){ return item.value === value })
            return !!(method && method.stdDev)
        },
        //El aviso nombra CUÁLES intervalos cayeron: sin barra que hoverear, el rango es lo
        //único que ubica el hueco en el gráfico. Por eso el formato tiene que ser EL MISMO
        //que el de las etiquetas del eje —SymbologyHistogram.formatRange(), es-CL con
        //labelDecimals—: un "1234.5678" acá contra un "1.234,57" en el gráfico nombra un
        //intervalo que el usuario no puede encontrar. Si formatRange cambia, esto también.
        //Computed y no string guardado: el selector de decimales puede moverse DESPUÉS de la
        //caída y el gráfico se reetiqueta; un string congelado quedaría mintiendo.
        histogramPartial: function(){
            if (!this.histogramFailed.length) return ''
            var bins = this.histogramBins
            var format = this.formatFieldValue
            //Separador ';' y no ',': con es-CL la coma ya es el separador decimal dentro de
            //cada rango.
            var list = this.histogramFailed.map(function(index){
                return format(bins[index].from) + ' - ' + format(bins[index].to)
            }).join('; ')
            return 'No se pudo contar ' + this.histogramFailed.length + ' de ' +
                   this.histogramBins.length + ' intervalos: ' + list + '.'
        },
        //Requisitos 14-19 de la spec en un solo párrafo. Se distingue SIEMPRE el rango visible
        //del rango completo: el mínimo y el máximo reales se informan aunque queden fuera del
        //gráfico, porque el clipping es visual y los datos no se tocan (D6).
        //Una cola cuya estadística falló entra como recortada pero sin primer valor: se dice
        //que hay datos fuera de la vista y se calla el valor que no se sabe, en vez de mentir.
        histogramRangeInfoItems: function(){
            var stats = this.histogramStats
            var visible = this.histogramVisible
            if (!stats || !visible) return []
            var format = this.formatFieldValue
            var clipped = this.histogramClipped || { low: null, high: null }
            var lowClipped = visible.from > stats.min
            var highClipped = visible.to < stats.max
            if (!lowClipped && !highClipped) {
                //Requisito 19: sin outliers no se comunica clipping en absoluto.
                return ['Rango visible: ' + format(visible.from) + ' a ' +
                        format(visible.to) + ' (todas las observaciones).']
            }
            var items = [
                'Rango visible: ' + format(visible.from) + ' a ' + format(visible.to) + '.',
                'Mínimo de las observaciones: ' + format(stats.min) +
                '; máximo: ' + format(stats.max) + '.',
            ]
            if (lowClipped) items.push(this.clippedText('bajo', clipped.low, stats.count))
            if (highClipped) items.push(this.clippedText('sobre', clipped.high, stats.count))
            items.push('Los valores recortados sólo salen del gráfico; los datos no se modifican.')
            return items
        },
    },
    methods:{
        rendererTypeLabel: function(renderer){
            var type = renderer ? renderer.type : ''
            return RENDERER_LABELS[type] || type || '—'
        },
        //El cliente Axios global agrega X-XSRF-TOKEN, cabecera que varios
        //servidores ArcGIS rechazan en CORS (ver FormularioArcgis.vue).
        fetchArcgis: async function(url){
            try {
                //Sin timeout un host inalcanzable deja la promesa colgada y la vista
                //no llega nunca al mensaje de servicio no disponible.
                const response = await fetch(url, { method: 'GET', credentials: 'omit', signal: AbortSignal.timeout(15000) })
                if (!response.ok) return null
                return await response.json()
            } catch (error) {
                console.log(error)
                return null
            }
        },
        checkLayerSupport: async function(){
            const meta = await this.fetchArcgis(UrlHandler.getArcgisUrl(this.layer) + '?f=json')
            this.metaLoaded = !!meta
            this.geometryType = (meta && meta.geometryType) || ''
            //Semilla gratis: el mismo documento trae el renderer propio del servicio...
            this.defaultRenderer = meta && meta.drawingInfo ? meta.drawingInfo.renderer : null
            //...y los campos con su tipo Esri.
            this.arcgisFields = (meta && meta.fields) || null
            this.maxRecordCount = (meta && meta.maxRecordCount) || 0

            //supportsDynamicLayers vive en la RAÍZ del servicio, no en el sublayer.
            const baseUrl = UrlHandler.getBaseUrl(this.layer)
            this.isMapServer = baseUrl.toLowerCase().endsWith('/mapserver')
            if (this.isMapServer) {
                const root = await this.fetchArcgis(baseUrl + '?f=json')
                this.supportsDynamic = !!(root && root.supportsDynamicLayers)
                //Muchos MapServer publican maxRecordCount sólo en la raíz. Se aprovecha la
                //petición que ya se hizo; no se agrega una nueva para FeatureServer.
                this.maxRecordCount = this.maxRecordCount || (root && root.maxRecordCount) || 0
            } else {
                this.supportsDynamic = true   //FeatureServer estiliza en cliente
            }
            this.checked = true
        },
        //ArcGIS 10.1+. Si el servicio la rechaza, el editor cae a entrada manual.
        //Devuelve { values, exceeded, count } o null: el truncamiento no se puede expresar
        //con un arreglo pelado. `count` es lo que devolvió el servicio antes de filtrar
        //nulos, que es la cifra que se compara contra el techo y la que se informa.
        fetchDistinctValues: async function(field){
            const url = UrlHandler.getArcgisUrl(this.layer) + '/query?where=1%3D1&outFields=' + encodeURIComponent(field) +
                        '&returnDistinctValues=true&returnGeometry=false&f=json'
            const data = await this.fetchArcgis(url)
            if (!data || data.error || !data.features) return null
            //Un nulo del servicio se convertiría en el valor literal "null".
            const values = data.features
                .map(function(feature){ return feature.attributes[field] })
                .filter(function(value){ return value !== null && value !== undefined })
            //Dos brazos: no todo servicio emite exceededTransferLimit en consultas distinct,
            //y tocar el techo es sospecha suficiente.
            const count = data.features.length
            const exceeded = data.exceededTransferLimit === true ||
                             (this.maxRecordCount > 0 && count >= this.maxRecordCount)
            return { values: values, exceeded: exceeded, count: count }
        },
        //outStatistics sobre el mismo /query siembra el rango de valores. min/max/count son
        //estadísticas que soporta cualquier servicio ArcGIS; avg y stddev NO (workspaces de
        //archivo, varios MapServer, campos numéricos publicados como texto), y un
        //statisticType rechazado bota la petición COMPLETA, no sólo esa estadística. Por eso
        //viven en fetchFieldMoments: si el servicio las rechaza se pierde el recorte de
        //outliers, no el rango — que es también lo que necesita la clasificación Manual.
        //`where` opcional: con él, la MISMA función devuelve la estadística restringida a un
        //subrango, que es lo que necesita el refinamiento de σ. Sin él se comporta igual que
        //antes.
        fetchMinMax: async function(field, where){
            const statistics = JSON.stringify([
                { statisticType: 'min', onStatisticField: field, outStatisticFieldName: 'min_value' },
                { statisticType: 'max', onStatisticField: field, outStatisticFieldName: 'max_value' },
                { statisticType: 'count', onStatisticField: field, outStatisticFieldName: 'count_value' },
            ])
            const url = UrlHandler.getArcgisUrl(this.layer) + '/query?returnGeometry=false&f=json' +
                        '&where=' + encodeURIComponent(where || '1=1') +
                        '&outStatistics=' + encodeURIComponent(statistics)
            const data = await this.fetchArcgis(url)
            if (!data || data.error || !data.features || !data.features.length) return null
            return data.features[0].attributes
        },
        //Hermana de fetchMinMax: mismo canal, mismo where opcional, misma forma de respuesta.
        //Separada para que su fallo sea aislable.
        fetchFieldMoments: async function(field, where){
            const statistics = JSON.stringify([
                { statisticType: 'avg', onStatisticField: field, outStatisticFieldName: 'avg_value' },
                { statisticType: 'stddev', onStatisticField: field, outStatisticFieldName: 'stddev_value' },
            ])
            const url = UrlHandler.getArcgisUrl(this.layer) + '/query?returnGeometry=false&f=json' +
                        '&where=' + encodeURIComponent(where || '1=1') +
                        '&outStatistics=' + encodeURIComponent(statistics)
            const data = await this.fetchArcgis(url)
            if (!data || data.error || !data.features || !data.features.length) return null
            return data.features[0].attributes
        },
        //Las dos peticiones en paralelo: el costo en tiempo de pared es el de una sola, que es
        //lo que costaba antes de partirlas. Sin momentos se devuelve igual el rango:
        //Number(undefined) es NaN y visibleRangeFor ya degrada a [min, max] con eso — el
        //histograma queda sin recorte de outliers, nunca en error.
        fetchFieldStats: async function(field, where){
            const [range, moments] = await Promise.all([
                this.fetchMinMax(field, where),
                this.fetchFieldMoments(field, where),
            ])
            if (!range) return null
            return Object.assign({}, range, moments || {})
        },
        //Sturges: ceil(1 + log2(n)). La razón no es estadística sino de presupuesto — cada
        //bin es UNA consulta HTTP returnCountOnly (ver fetchBinCounts). Sturges da 8 bins a
        //n=100 y 18 a n=100.000, dentro del techo de 20 que ya se pagaba; la regla de Rice
        //pediría ~93 consultas a n=100.000 y Scott ~80. Y no necesita IQR ni percentiles,
        //cuyo soporte en el ArcGIS del proveedor no es verificable desde acá.
        //El clamp [5, 25] cubre los dos extremos: con n<16 Sturges cae a 5 y menos bins no
        //dibujan una distribución; 25 es el techo de peticiones aceptado.
        binCountFor: function(count){
            var n = Number(count)
            //Sin conteo utilizable se usa el piso del clamp, no un número inventado: pocas
            //barras anchas son legibles, y no reintroduce el 20 fijo que este ticket quita.
            if (!Number.isFinite(n) || n < 1) return 5
            return Math.min(25, Math.max(5, Math.ceil(1 + Math.log2(n))))
        },
        //Rango visible = μ ± 3σ intersectado con [min, max], cada extremo por separado
        //(decisión D5 de la spec: una cola sin outliers no se recorta). Regla ÚNICA para los
        //seis break methods, incluido `manual`: no hay tabla por método que pueda quedar
        //incompleta, y el caché de bins por campo se conserva porque el rango no depende de
        //la clasificación elegida.
        //Sin avg/stddev utilizables no se recorta nada: el histograma degrada al
        //comportamiento actual (rango completo), nunca a un error.
        //Se llama DOS veces: sobre la estadística total y sobre la del rango provisional. El
        //acotado a [min, max] del insumo es justo lo que hace útil la segunda pasada.
        visibleRangeFor: function(stats){
            var min = Number(stats.min_value)
            var max = Number(stats.max_value)
            var avg = Number(stats.avg_value)
            var sd = Number(stats.stddev_value)
            if (!Number.isFinite(avg) || !Number.isFinite(sd) || sd <= 0) return { from: min, to: max }
            var from = Math.max(min, avg - 3 * sd)
            var to = Math.min(max, avg + 3 * sd)
            //Cinturón: con avg fuera de [min, max] por redondeo del servicio el rango podría
            //invertirse y las 20 consultas saldrían todas en 0.
            if (!(to > from)) return { from: min, to: max }
            return { from: from, to: to }
        },
        //Conteos exactos, no una muestra. La alternativa de una sola consulta con outFields
        //y binning local devuelve maxRecordCount filas EN ORDEN DE OID, que no es una muestra
        //aleatoria: el histograma mostraría una distribución falsa sin decírselo a nadie.
        //La CANTIDAD de bins ya no es fija: sale de binCountFor(count) — y como cada bin es
        //una consulta, esa función es también el presupuesto de peticiones de este método.
        fetchBinCounts: async function(field){
            var token = ++this.histogramToken
            this.histogramLoading = true
            this.histogramError = ''
            this.histogramFailed = []
            this.histogramBins = []
            this.histogramStats = null
            this.histogramVisible = null
            this.histogramClipped = null
            const stats = await this.fetchFieldStats(field)
            if (token !== this.histogramToken || this.mode !== 'edit') return
            var min = stats ? Number(stats.min_value) : NaN
            var max = stats ? Number(stats.max_value) : NaN
            //Degenerado: sin rango no hay bins que armar. CERO peticiones de conteo, y
            //el editor sigue completamente usable — el histograma es una ayuda, nunca
            //una barrera.
            if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
                this.histogramError = 'No fue posible calcular la distribución del campo.'
                this.histogramLoading = false
                return
            }
            //El rango que se DIBUJA puede ser más angosto que [min, max]: los outliers se
            //recortan de la vista, nunca de los datos (D6 de la spec).
            var visible = this.visibleRangeFor(stats)
            //UNA iteración de sigma clipping, y sólo si la primera pasada recortó algo. σ sobre
            //TODAS las observaciones está inflado por los propios outliers: con 9.999 valores en
            //[0,100] y uno en 50.000, σ≈501 y μ+3σ≈1.557, así que el grueso de los datos caería
            //en una sola barra. Recalcular μ/σ sobre el rango provisional —que ya excluye los
            //extremos— y acotar al min/max de ESE subconjunto es lo que baja el techo al dato
            //real. Sin outliers no se dispara ninguna petición y el camino queda como hoy.
            if (visible.from > min || visible.to < max) {
                //toFixed(10) y no `step` (que aún no existe: depende del ancho final). El techo
                //es un rango provisional < 1e-10, donde los dos bordes colapsan al mismo número,
                //el subconjunto sale vacío y el guard de count deja el rango sin refinar — que
                //es exactamente la degradación deseada.
                var subWhere = field + ' >= ' + visible.from.toFixed(10) +
                               ' AND ' + field + ' <= ' + visible.to.toFixed(10)
                const subStats = await this.fetchFieldStats(field, subWhere)
                if (token !== this.histogramToken || this.mode !== 'edit') return
                if (subStats && Number(subStats.count_value) > 0) {
                    var refined = this.visibleRangeFor(subStats)
                    //Un subconjunto de valores todos iguales daría un rango degenerado: se
                    //descarta el refinamiento antes que quedarse sin bins que armar.
                    if (refined.to > refined.from) visible = refined
                }
            }
            //Sturges sobre el conteo TOTAL del campo, no el del subconjunto: es una regla de
            //legibilidad sobre el tamaño del conjunto, y usar el subconjunto obligaría a pedir
            //la estadística refinada también cuando no hay recorte.
            var binCount = this.binCountFor(stats.count_value)
            var width = (visible.to - visible.from) / binCount
            //Dos dígitos bajo el ancho del bin: suficiente para que la costura entre
            //bins vecinos no deje hueco ni solape, sin arrastrar la basura binaria del
            //flotante al where. Tope 20 y no 10: con un rango diminuto (max-min ~1e-9)
            //diez decimales redondean todos los bordes al MISMO número y las consultas
            //salen todas en 0 — un caso que hoy funciona y que el tope rompería.
            //El techo de 1e21 (donde toFixed sigue emitiendo exponente) se acepta como
            //residuo: no es dato geográfico plausible.
            var step = Math.min(20, Math.max(0, -Math.floor(Math.log10(width)) + 2))
            var unit = Math.pow(10, -step)
            var edges = []
            //Un solo arreglo de binCount+1 bordes: from del bin i y to del bin i-1 son EL
            //MISMO número, no dos redondeos que podrían separarse.
            for (var i = 0; i <= binCount; i++) edges.push(Number((visible.from + i * width).toFixed(step)))
            //Los extremos no se redondean al vecino más cercano: cuando NO hay recorte,
            //visible.from/to son min y max, que SON valores reales de features. Si el borde
            //sube, la feature que vale exactamente min no satisface el where de ningún bin y
            //la suma de los conteos deja de cuadrar con el total. Cuando SÍ hay recorte el
            //redondeo hacia afuera es inocuo: mueve el borde menos de un centésimo de bin.
            edges[0] = Number((Math.floor(visible.from / unit) * unit).toFixed(step))
            edges[binCount] = Number((Math.ceil(visible.to / unit) * unit).toFixed(step))
            var ranges = []
            for (var i = 0; i < binCount; i++) {
                ranges.push({ from: edges[i], to: edges[i + 1] })
            }
            var base = UrlHandler.getArcgisUrl(this.layer)
            //Las estadísticas de cola viajan en la MISMA ola que los conteos: los límites ya
            //se conocen, y el guard de token de abajo las cubre sin necesidad de uno nuevo.
            //`low` pide el MAYOR valor bajo el rango visible, `high` el MENOR sobre él: el
            //primer valor recortado que el usuario encuentra al salir de la vista.
            var lowClipped = edges[0] > min
            var highClipped = edges[binCount] < max
            var tailUrls = [
                lowClipped ? this.tailStatUrl(base, field, 'max', field + ' < ' + edges[0].toFixed(step)) : null,
                highClipped ? this.tailStatUrl(base, field, 'min', field + ' > ' + edges[binCount].toFixed(step)) : null,
            ]
            const responses = await Promise.all(
                ranges.map((range, index) => {
                    //El último bin cierra con <= para no perder el valor máximo por
                    //aritmética de punto flotante (y, con recorte, para incluir el borde).
                    var operator = index === ranges.length - 1 ? '<=' : '<'
                    var where = field + ' >= ' + range.from.toFixed(step) +
                                ' AND ' + field + ' ' + operator + ' ' + range.to.toFixed(step)
                    var url = base + '/query?returnCountOnly=true&returnGeometry=false&f=json' +
                              '&where=' + encodeURIComponent(where)
                    return this.fetchArcgis(url)
                }).concat(tailUrls.map(url => url ? this.fetchArcgis(url) : Promise.resolve(null)))
            )
            if (token !== this.histogramToken || this.mode !== 'edit') return
            this.histogramLoading = false
            var counts = responses.slice(0, ranges.length)
            var tails = responses.slice(ranges.length)
            //Un conteo caído YA NO es un cero: null dice "no se sabe" y ApexCharts no
            //dibuja barra para null. El aviso de abajo nombra CUÁLES cayeron, porque sin
            //barra no hay nada que hoverear y "3 de 12" a secas no dice dónde está el hueco.
            var failed = []
            counts.forEach(function(data, index){
                if (!data || data.error || data.count == null) failed.push(index)
            })
            //Si el servicio rechazó TODAS no hay gráfico que mostrar: el mensaje reemplaza
            //el bloque entero, como hasta ahora.
            if (failed.length === ranges.length) {
                this.histogramError = 'No fue posible calcular la distribución del campo.'
                return
            }
            this.histogramField = field
            this.histogramBins = ranges.map(function(range, index){
                var data = counts[index]
                return { from: range.from, to: range.to, count: data && data.count != null ? data.count : null }
            })
            this.histogramFailed = failed
            this.histogramStats = {
                min: min, max: max,
                count: Number(stats.count_value),
            }
            this.histogramVisible = { from: edges[0], to: edges[binCount] }
            //Una cola caída se degrada como histogramFailed, NO como histogramError: el
            //gráfico ya está bien y sólo se pierde una línea de texto. null = "no se sabe".
            this.histogramClipped = {
                low: lowClipped ? { value: this.tailValue(tails[0], 'tail_value'),
                                     count: this.tailValue(tails[0], 'tail_count') } : null,
                high: highClipped ? { value: this.tailValue(tails[1], 'tail_value'),
                                       count: this.tailValue(tails[1], 'tail_count') } : null,
            }
        },
        //Una sola consulta outStatistics por cola: el mismo canal que fetchMinMax, con where.
        tailStatUrl: function(base, field, statisticType, where){
            var statistics = JSON.stringify([
                { statisticType: statisticType, onStatisticField: field, outStatisticFieldName: 'tail_value' },
                { statisticType: 'count', onStatisticField: field, outStatisticFieldName: 'tail_count' },
            ])
            return base + '/query?returnGeometry=false&f=json' +
                   '&where=' + encodeURIComponent(where) +
                   '&outStatistics=' + encodeURIComponent(statistics)
        },
        tailValue: function(data, key){
            if (!data || data.error || !data.features || !data.features.length) return null
            var raw = data.features[0].attributes[key]
            //Number(null) es 0 y Number.isFinite(0) es true: sin este guard, una respuesta con
            //el atributo nulo imprimiría "Primer valor recortado: 0,00", un dato inventado.
            if (raw == null) return null
            var value = Number(raw)
            return Number.isFinite(value) ? value : null
        },
        //Una sola línea por cola: el primer valor recortado ES uno de los extremos del tramo
        //recortado, y el otro ya lo nombra la viñeta de mínimo/máximo, así que la versión
        //anterior imprimía el mismo número en dos viñetas seguidas.
        //Sin valor (la estadística de la cola falló) se informa igual que hay observaciones
        //fuera de la vista: callarlo escondería datos, inventar un valor mentiría.
        clippedText: function(side, tail, total){
            var percent = tail && tail.count != null ? this.formatPercent(tail.count, total) : ''
            var suffix = percent ? ' (' + percent + ' de las observaciones)' : ''
            if (!tail || tail.value == null) {
                return 'Hay observaciones recortadas ' + side + ' el rango visible' + suffix + '.'
            }
            return 'Primer valor recortado ' + side + ' el rango: ' +
                   this.formatFieldValue(tail.value) + suffix + '.'
        },
        //Sin denominador utilizable NO se imprime un porcentaje: se devuelve '' y el llamador
        //omite la cláusula entera. count_value nulo da 0 y ausente da NaN — los dos caen acá,
        //y los dos imprimían "(0,0% de las observaciones)", una cifra inventada.
        formatPercent: function(count, total){
            var n = Number(total)
            if (!Number.isFinite(n) || n <= 0) return ''
            return (count / n * 100).toLocaleString('es-CL', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            }) + '%'
        },
        //Mismo formato que SymbologyHistogram.formatRange(): es-CL con labelDecimals. Si
        //divergiera, un "1234.5678" acá contra un "1.234,57" en el gráfico nombraría un
        //intervalo que el usuario no puede encontrar.
        formatFieldValue: function(value){
            return Number(value).toLocaleString('es-CL', {
                minimumFractionDigits: this.labelDecimals,
                maximumFractionDigits: this.labelDecimals,
            })
        },
        //Perezoso: abrir "Editar" sobre una simbología guardada ya trae campo, así que
        //un histograma ansioso cobraría 21 peticiones a todo el que sólo entró a
        //cambiar un color.
        toggleHistogram: function(){
            this.histogramOpen = !this.histogramOpen
            if (this.histogramOpen && this.draft.field && this.histogramField !== this.draft.field && !this.histogramLoading) {
                this.fetchBinCounts(this.draft.field)
            }
        },
        //ArcGIS calcula los cortes: cero estadística propia y una petición en vez de N
        //(decisión 2). baseSymbol y colorRamp van porque varias versiones los declaran
        //REQUERIDOS dentro de classBreaksDef; se mandan y se botan — el color lo pone la
        //paleta (decisión 8). Omitirlos haría fallar TODA petición y el camino de error
        //lo reportaría como "el servicio no soporta la operación", escondiendo la causa.
        //El baseSymbol describe la geometría del sublayer y la geometría de la capa es la
        //única fuente correcta: un esriSFS fijo la contradice en puntos y en líneas.
        fetchGenerateRenderer: async function(field){
            var selected = this.classificationMethod
            var method = CLASSIFICATION_METHODS.find(function(item){ return item.value === selected })
            if (!method || !method.esri) return null
            var definition = {
                type: 'classBreaksDef',
                classificationField: field,
                classificationMethod: method.esri,
                baseSymbol: defaultSymbolFor(this.geometryType),
                colorRamp: {
                    type: 'algorithmic', algorithm: 'esriHSVAlgorithm',
                    fromColor: [255, 255, 255, 255], toColor: [0, 0, 0, 255],
                },
            }
            if (method.stdDev) definition.standardDeviationInterval = Number(this.standardDeviationInterval)
            else definition.breakCount = Number(this.breakCount)

            const url = UrlHandler.getArcgisUrl(this.layer) + '/generateRenderer?f=json' +
                        '&classificationDef=' + encodeURIComponent(JSON.stringify(definition))
            const data = await this.fetchArcgis(url)
            if (!data || data.error || !Array.isArray(data.classBreakInfos)) return null
            //El renderer completo, no sólo la lista: algunas versiones publican el piso
            //en renderer.minValue y no en classBreakInfos[0].classMinValue (decisión 9).
            return data
        },
        //Entra desde cuatro controles (método, clases, intervalo, campo): el guard vive
        //acá, no en cada llamador. Reemplaza classBreakInfos ENTERO (decisión 7): elegir
        //un método ES pedir que se recorten los rangos.
        applyClassification: async function(){
            //Se invalida lo en vuelo ANTES de los guards: elegir "Manual" o vaciar el número
            //de clases sale por un return, pero es igual de explícito que la petición
            //anterior ya no se quiere — si no, su respuesta reemplaza los rangos que el
            //usuario ya empezó a editar a mano.
            var token = ++this.classificationToken
            if (!this.draft.field || this.classificationMethod === 'manual') return
            //min="2" en un <input> suelto no valida nada: sin <form> ni submit, borrar el
            //campo deja breakCount en '' y JSON.stringify emite null → 400 → el mensaje de
            //la decisión 3 culparía al servidor por un error nuestro.
            if (!this.isStdDevMethod && !(Number(this.breakCount) >= 2)) {
                //breakCount sobrevive al cambio de método: sin aviso, vaciarlo una vez deja
                //el selector inerte para siempre y nada en pantalla lo explica.
                this.message = 'Ingrese un número de clases de al menos 2.'
                return
            }
            //Lo pedido se captura ANTES del await: después, el estado ya puede ser el de la
            //petición siguiente y el aviso de conteo compararía contra el número equivocado.
            var askedBreaks = Number(this.breakCount)
            var askedStdDev = this.isStdDevMethod
            const renderer = await this.fetchGenerateRenderer(this.draft.field)
            //Token distinto = esta respuesta es de una petición ya reemplazada, o el draft
            //nació de nuevo mientras viajaba. `mode` ataja la que aterriza con el editor ya
            //cerrado: escribiría message sobre la lista y mutaría un draft descartado.
            if (token !== this.classificationToken || this.mode !== 'edit') return
            const breaks = renderer ? renderer.classBreakInfos : null
            if (!breaks || !breaks.length) {
                this.message = 'El servicio no permite calcular los cortes automáticamente: ingrese los rangos a mano.'
                return
            }
            this.message = ''
            //Sólo cortes y etiquetas (decisión 8): el símbolo del servicio puede ser un
            //esriPFS o una referencia CIM que el editor no sabe manipular — el mismo caso
            //que isSeedUsable() ataja. La etiqueta va verbatim (decisión 9).
            //`== null` y no `=== undefined`: un servicio que emite classMinValue: null se
            //saltaría el respaldo, normalizedDraft borraría la llave y la simbología saldría
            //sin piso, que es justo lo que la decisión 9 evita.
            var floor = breaks[0].classMinValue
            if (floor == null) floor = renderer.minValue
            this.$set(this.draft, 'minValue', floor == null ? '' : floor)
            this.$set(this.draft, 'classBreakInfos', breaks.map((info) => ({
                classMaxValue: info.classMaxValue,
                //Sin `label`: la etiqueta la escribe el editor (decisión 2, deroga la mitad
                //de QA-25 D9). Copiarla verbatim es lo que dejaba "Estándar Desv." en la
                //leyenda y lo que no dejaba dónde aplicar los decimales.
                label: info.label,
                symbol: defaultSymbolFor(this.geometryType),
            })))
            //setPalette() asigna this.paletteId como primera línea: el `||` enciende la
            //secuencial por defecto cuando no había paleta (decisión 12) y repinta en un
            //solo acto, igual que addClassBreak(). No contradice QA-24 D5: estas filas
            //nacieron hace un instante, no hay color de nadie que proteger.
            this.setPalette(this.paletteId || defaultPaletteFor('classBreaks'))
            //La inferencia acierta el defecto; deja de correr apenas el usuario toca el
            //selector (decisión 5).
            if (!this.decimalsTouched) this.labelDecimals = inferDecimals(this.draft.classBreakInfos)
            this.regenerateLabels()
            //Último en escribir message: con pocos valores distintos el servicio colapsa
            //cortes y devuelve menos (decisión 11). En Desviación Estándar no aplica —
            //ahí nunca se pidió un número.
            if (!askedStdDev && breaks.length !== askedBreaks) {
                this.message = 'El servicio devolvió ' + breaks.length + ' rangos en vez de los ' +
                               askedBreaks + ' solicitados: el campo no tiene suficientes valores distintos.'
            }
        },
        onMethodChange: function(value){
            this.classificationMethod = value
            this.applyClassification()
        },
        onStdDevIntervalChange: function(value){
            //El value de una <option> es string aunque la lista sean números.
            this.standardDeviationInterval = Number(value)
            this.applyClassification()
        },
        //Un renderer de un tipo ajeno (heatmap, dotDensity) o al que le falta
        //la lista que el editor manipula rompería el formulario: mejor partir
        //de un símbolo único vacío.
        isSeedUsable: function(renderer){
            if (!renderer) return false
            var usable = false
            if (renderer.type === 'simple') usable = !!renderer.symbol
            else if (renderer.type === 'uniqueValue') usable = Array.isArray(renderer.uniqueValueInfos) && !!renderer.defaultSymbol
            else if (renderer.type === 'classBreaks') usable = Array.isArray(renderer.classBreakInfos)
            if (!usable) return false
            //El editor de polígonos sólo sabe manipular esriSFS (relleno sólido).
            //Un esriPFS (relleno con imagen) o una referencia CIM del servicio se
            //guardaría intacto aunque el usuario crea que edita color y contorno, y
            //en el camino FeatureServer symbolToOlStyle devuelve undefined sin
            //fallbackStyle: la feature no se dibuja y no hay error. Vale para las
            //tres formas de renderer, no sólo para simple.
            if (this.geometryType === 'esriGeometryPolygon') {
                return rendererSymbols(renderer).every(function(symbol){ return symbol && symbol.type === 'esriSFS' })
            }
            return true
        },
        //Único punto que decide el color de una clase que NACE (decisión 4.1). Sin
        //paleta activa devuelve el naranja de siempre, que es lo que pasa al editar
        //una simbología guardada hasta que el usuario elija paleta.
        classSymbol: function(index, total){
            var symbol = defaultSymbolFor(this.geometryType)
            var colors = this.paletteColors
            if (colors) {
                applyColor(symbol, this.draft.type === 'uniqueValue'
                    ? qualitativeColor(colors, index)
                    : sequentialColor(colors, index, total))
            }
            return symbol
        },
        //Repinta TODAS las clases de la rama activa (decisión 4.2). Es el único acto
        //que borra retoques manuales, y por eso es explícito: no se repinta ante
        //cualquier cambio de la lista. El defaultSymbol queda fuera (decisión 10):
        //no es una categoría, es el resto, y con color de paleta quedaría
        //indistinguible de una categoría real en la leyenda.
        setPalette: function(id){
            this.paletteId = id
            var colors = this.paletteColors
            if (!colors) return
            if (this.draft.type === 'uniqueValue') {
                this.draft.uniqueValueInfos.forEach(function(info, index){
                    applyColor(info.symbol, qualitativeColor(colors, index))
                })
                return
            }
            //La rampa sigue el orden de los máximos, no la posición en el arreglo
            //(decisión 9): con los máximos tecleados 5000, 1000, 20000, asignar por
            //posición diría "1000 es más oscuro que 5000". Las filas NO se reordenan:
            //hacerlo en vivo las haría saltar bajo el cursor.
            var infos = this.draft.classBreakInfos
            var order = infos.map(function(info, index){ return index })
                .sort(function(a, b){ return infos[a].classMaxValue - infos[b].classMaxValue })
            order.forEach(function(arrayIndex, rank){
                applyColor(infos[arrayIndex].symbol, sequentialColor(colors, rank, infos.length))
            })
        },
        //Reescribe TODAS las etiquetas de la rama classBreaks (decisión 6), incluidas
        //las tecleadas a mano. Es local y sin red: se cuelga de los mismos disparadores
        //que setPalette() más el selector de decimales y "Valor mínimo". Distinguir una
        //etiqueta personalizada exigiría pattern matching contra un string formateado
        //por locale, que es frágil — y es el mismo contrato que la vista ya tiene con
        //setPalette() repintando todas las clases.
        regenerateLabels: function(){
            if (!this.draft || this.draft.type !== 'classBreaks') return
            var labels = formatBreakLabels(this.draft.classBreakInfos, this.draft.minValue, this.labelDecimals)
            //$set por fila: `info` ya es reactivo, pero la llave `label` puede no existir
            //en un renderer legado que nunca la trajo.
            labels.forEach((label, index) => { this.$set(this.draft.classBreakInfos[index], 'label', label) })
        },
        onDecimalsChange: function(value){
            //El value de una <option> es string aunque la lista sean números — mismo
            //motivo que onStdDevIntervalChange().
            this.labelDecimals = Number(value)
            //Desde acá la inferencia no vuelve a correr en esta sesión de edición.
            this.decimalsTouched = true
            this.regenerateLabels()
        },
        //Los mismos tres nacimientos de draft que resetean paletteId. El método NO se
        //persiste (decisión 10): al abrir "Editar" el selector arranca en Manual y se
        //ven los rangos guardados tal cual.
        resetClassification: function(){
            //Un draft nuevo invalida cualquier cálculo en vuelo: sin esto la respuesta tardía
            //escribe classBreakInfos y minValue sobre el draft que la reemplazó — y sobre un
            //renderer 'simple' esas llaves ni siquiera pasan por normalizedDraft.
            this.classificationToken++
            this.classificationMethod = 'manual'
            this.breakCount = 5
            this.standardDeviationInterval = 1
            //Draft nuevo = inferencia habilitada de nuevo. Los mismos tres nacimientos.
            this.decimalsTouched = false
            //Mismo motivo que classificationToken: un draft nuevo invalida cualquier
            //tanda de conteos en vuelo del draft reemplazado.
            this.histogramToken++
            this.histogramOpen = false
            this.histogramLoading = false
            this.histogramError = ''
            this.histogramFailed = []
            this.histogramField = ''
            this.histogramBins = []
        },
        newSymbology: function(){
            this.message = ''
            this.editingId = null
            this.draftName = ''
            //El renderer del propio servicio es la mejor semilla disponible,
            //siempre que sea de un tipo que el editor sabe manipular.
            this.draft = this.isSeedUsable(this.defaultRenderer)
                ? JSON.parse(JSON.stringify(this.defaultRenderer))
                : { type: 'simple', symbol: defaultSymbolFor(this.geometryType) }
            //El preview bindea this.draft, no normalizedDraft: sin esto el editor
            //muestra una semilla esriSFSNull como relleno opaco y el usuario que
            //mueve la opacidad a 100% pierde su cambio al guardar, porque el style
            //sigue siendo Null y transparentize lo vuelve a 0. Normalizando acá, el
            //editor abre diciendo la verdad (0% de opacidad) y cualquier edición
            //posterior sobrevive: el style ya es Solid.
            normalizeSymbolStyles(this.draft)
            //La semilla del servicio NO se repinta (decisión 15): puede traer N
            //categorías ya coloreadas por quien publicó el servicio, muchas veces con
            //los colores oficiales de la institución. Sólo se deja la paleta activa
            //para las clases que nazcan después.
            this.paletteId = defaultPaletteFor(this.draft.type)
            this.resetClassification()
            this.mode = 'edit'
        },
        editSymbology: function(item){
            this.message = ''
            this.editingId = item.id
            this.draftName = item.name
            this.draft = JSON.parse(JSON.stringify(item.renderer))
            //Sin paleta activa (decisión 5): la simbología guardada ya tiene colores
            //elegidos por alguien y repintarlos al abrir los destruye sin que nadie
            //lo pida.
            this.paletteId = ''
            this.resetClassification()
            //Después de resetClassification(), que baja decimalsTouched: la inferencia
            //corre sobre las etiquetas guardadas, no sobre las que este método escribe.
            //Una simbología guardada antes de este cambio trae etiquetas de ArcGIS: no
            //hay coincidencia es-CL y cae a la regla de la decisión 5. Nada se persiste
            //hasta que el usuario presione "Guardar" (decisión 15).
            if (this.draft.type === 'classBreaks') {
                var infos = Array.isArray(this.draft.classBreakInfos) ? this.draft.classBreakInfos : []
                var saved = infos.length ? inferDecimalsFromLabel(infos[0].label) : null
                this.labelDecimals = saved == null ? inferDecimals(infos) : saved
                this.regenerateLabels()
            }
            this.mode = 'edit'
        },
        setRendererType: function(type){
            if (type === 'uniqueValue') {
                this.draft = { type: type, field1: '', uniqueValueInfos: [], defaultSymbol: defaultSymbolFor(this.geometryType) }
            } else if (type === 'classBreaks') {
                //minValue vacío = sin piso, que es el -Infinity que createStyleFunction ya
                //contempla cuando la clave está ausente (symbology.js:218). Sembrar 0 escondía
                //toda feature negativa sin que nadie lo eligiera.
                this.draft = { type: type, field: '', minValue: '', classBreakInfos: [] }
            } else {
                this.draft = { type: 'simple', symbol: defaultSymbolFor(this.geometryType) }
            }
            this.paletteId = defaultPaletteFor(type)
            this.resetClassification()
        },
        addUniqueValue: function(){
            this.draft.uniqueValueInfos.push({
                value: '', label: '',
                symbol: this.classSymbol(this.freeColorIndex())
            })
        },
        //El largo del arreglo como índice asume que sólo crece; con "Quitar" no es
        //cierto: quitar la fila 1 de 5 deja los colores [0,2,3,4] y el índice 4 ya está
        //en uso. No repinta nada existente, así que la decisión 4 sigue intacta acá.
        //Se evalúa como argumento ANTES del push: la fila nueva todavía no está en la
        //lista y no se cuenta a sí misma. Pintar después del push devolvería el duplicado.
        freeColorIndex: function(){
            var colors = this.paletteColors
            var used = this.draft.uniqueValueInfos.map(function(info){
                return info.symbol ? rgbToHex(info.symbol.color) : ''
            })
            if (colors) {
                for (var i = 0; i < colors.length; i++) {
                    if (used.indexOf(qualitativeColor(colors, i)) === -1) return i
                }
            }
            return used.length
        },
        addClassBreak: function(){
            this.draft.classBreakInfos.push({
                classMaxValue: 0, label: '',
                symbol: defaultSymbolFor(this.geometryType)
            })
            this.setPalette(this.paletteId)
            this.regenerateLabels()
        },
        removeClassBreak: function(index){
            this.draft.classBreakInfos.splice(index, 1)
            this.setPalette(this.paletteId)
            this.regenerateLabels()
        },
        onUniqueFieldChange: async function(field){
            this.$set(this.draft, 'field1', field)
            this.$set(this.draft, 'uniqueValueInfos', [])
            if (!field) return
            const result = await this.fetchDistinctValues(field)
            if (result === null) {
                this.message = 'El servicio no permite consultar valores distintos: ingrese los valores a mano.'
                return
            }
            //El aviso sobrevive hasta el próximo cambio de campo: la lista se ve completa y no
            //hay ninguna otra señal de que falten valores.
            this.message = result.exceeded
                ? 'El servicio devolvió sólo los primeros ' + result.count + ' valores distintos: ' +
                  'puede haber más. Agregue a mano los que falten o revise el símbolo por defecto.'
                : ''
            //Esri guarda los valores como string; symbology.js coacciona igual.
            this.$set(this.draft, 'uniqueValueInfos', result.values.map((value, index) => ({
                value: String(value),
                label: String(value),
                symbol: this.classSymbol(index, result.values.length),
            })))
        },
        onClassFieldChange: async function(field){
            this.$set(this.draft, 'field', field)
            //Los bins dependen SÓLO del campo. Si el panel está cerrado no cuesta nada: se
            //invalida el caché y la tanda se dispara recién en la primera expansión, donde
            //toggleHistogram compara histogramField contra draft.field.
            if (this.histogramOpen && field) this.fetchBinCounts(field)
            else { this.histogramField = ''; this.histogramBins = []; this.histogramError = ''; this.histogramFailed = [] }
            if (!field) return
            //Con método ≠ Manual el minValue sale de la respuesta (decisión 9) y la
            //consulta min/max sobra. La rama Manual sigue idéntica a como estaba.
            if (this.classificationMethod !== 'manual') {
                //Los máximos del campo anterior no clasifican el nuevo: si el cálculo falla,
                //dejarlos permite guardar un renderer que miente sobre qué campo clasifica
                //(validateDraft sólo exige campo y lista no vacía). El piso corre la misma
                //suerte: '' es la semilla de setRendererType y normalizedDraft borra la llave,
                //así que createStyleFunction cae a -Infinity en vez de esconder features bajo
                //un piso ajeno. Si la clasificación tiene éxito, /generateRenderer lo repuebla.
                this.$set(this.draft, 'classBreakInfos', [])
                this.$set(this.draft, 'minValue', '')
                return this.applyClassification()
            }
            const stats = await this.fetchMinMax(field)
            if (!stats) {
                this.message = 'El servicio no permite consultar el rango del campo: ingrese los valores a mano.'
                return
            }
            this.message = ''
            this.$set(this.draft, 'minValue', stats.min_value)
            if (!this.draft.classBreakInfos.length) {
                this.$set(this.draft, 'classBreakInfos', [{ classMaxValue: stats.max_value, label: '', symbol: this.classSymbol(0, 1) }])
            }
        },
        //Invariante del editor: classBreaks siempre sale ordenado ascendente. El piso es
        //opcional: sin minValue, symbology.js usa -Infinity.
        normalizedDraft: function(){
            var renderer = JSON.parse(JSON.stringify(this.draft))
            if (renderer.type === 'uniqueValue') {
                renderer.uniqueValueInfos = renderer.uniqueValueInfos.map(function(info){
                    info.value = String(info.value)
                    return info
                })
            }
            if (renderer.type === 'classBreaks') {
                var floor = Number(renderer.minValue)
                //'' , null y NaN significan "sin piso": la clave sale del renderer en vez de
                //colapsar a 0, que es un piso real y esconde los negativos.
                if (renderer.minValue === '' || renderer.minValue == null || !Number.isFinite(floor)) {
                    delete renderer.minValue
                } else {
                    renderer.minValue = floor
                }
                renderer.classBreakInfos = renderer.classBreakInfos
                    .map(function(info){
                        info.classMaxValue = Number(info.classMaxValue)
                        return info
                    })
                    .sort(function(a, b){ return a.classMaxValue - b.classMaxValue })
            }
            //La semilla del servicio puede traer un estilo que el editor nunca ofrece
            //(esriSFSNull, achurados, esriSLSLongDash): sin esto se guarda intacto
            //aunque el usuario crea que edita el color. geometry-blind a propósito:
            //condicionar a esriSFS costaría más que aplicarlo a los dos tipos.
            normalizeSymbolStyles(renderer)
            return renderer
        },
        validateDraft: function(){
            if (!this.draftName.trim()) return 'Ingrese un nombre para la simbología.'
            if (this.draft.type === 'uniqueValue') {
                if (!this.draft.field1) return 'Seleccione el campo de los valores únicos.'
                if (!this.draft.uniqueValueInfos.length) return 'Agregue al menos un valor.'
            }
            if (this.draft.type === 'classBreaks') {
                if (!this.draft.field) return 'Seleccione el campo numérico de los rangos.'
                if (!this.draft.classBreakInfos.length) return 'Agregue al menos un rango.'
                var invalid = this.draft.classBreakInfos.some(function(info){
                    return !Number.isFinite(Number(info.classMaxValue))
                })
                if (invalid) return 'Todos los rangos deben tener un valor máximo numérico.'
            }
            //El traductor devuelve undefined para un esriPMS sin src (symbology.js:128) y el
            //camino de simbología elegida no tiene respaldo (D12): la feature no se dibuja, sin
            //error en consola ni en red. El editor es el único punto que puede atajarlo.
            var noImage = rendererSwatches(this.draft).some(function(row){
                return row.symbol && row.symbol.type === 'esriPMS' && !row.symbol.imageData && !row.symbol.url
            })
            if (noImage) return 'Hay un símbolo de tipo "Ícono" sin imagen cargada: suba una imagen o cambie el tipo a "Forma simple".'
            return ''
        },
        save: function(){
            var error = this.validateDraft()
            if (error) {
                this.message = error
                return
            }
            var that = this
            var body = { name: this.draftName.trim(), renderer: this.normalizedDraft() }
            var request = this.editingId
                ? this.putSymbology(this.editingId, body)
                : this.postSymbology(body)

            request
                .then(function(){
                    that.message = ''
                    that.mode = 'list'
                    return that.getSymbologies()
                })
                .catch(function(err){
                    that.message = that.symbologyError(err)
                })
        },
        confirmDelete: function(item){
            var that = this
            this.$modal.show('dialog', {
                text: '¿Desea eliminar la simbología "' + item.name + '"?',
                buttons: [
                    {
                        title: 'No',
                        handler: () => { that.$modal.hide('dialog') }
                    },
                    {
                        title: 'Sí',
                        handler: () => {
                            that.$modal.hide('dialog')
                            that.deleteSymbology(item.id)
                                .then(function(){ return that.getSymbologies() })
                                .catch(function(err){ that.message = that.symbologyError(err) })
                        }
                    }
                ]
            })
        },
    },
    created () {
        this.$store.getters.getPermissions.layers? true:this.$router.push('/pagina-no-encontrada')
    },
    mounted () {
        this.getLayer();
    },
    watch: {
        //El backend devuelve el documento crudo de Mongo (`FindOneLayer` decodifica
        //en bson.M): la llave es `_id`, no `id`.
        'layer': function (newValue) {
            if(newValue._id){
                this.checkLayerSupport();
                this.getSymbologies();
            }
        },
    }
}
</script>

<style lang="scss" scoped>

.sym-message {
    margin-bottom: 0.75rem;
    color: var(--color-orange);
    font-size: 0.9rem;
}

.sym-blocked,
.sym-empty,
.sym-hint {
    color: var(--color-ghost);
    font-size: 0.9rem;
    padding: 0.5rem 0;
}

ul.sym-hint {
    margin: 0;
    padding-left: 1.1em;
}

.sym-histogram-title {
    margin: 8px 0 4px;
    font-size: 14px;
    font-weight: 600;
}

.sym-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 0.75rem;

    th, td {
        text-align: left;
        padding: 0.5rem 0.75rem;
        border-bottom: 1px solid var(--color-line);
        font-size: 0.9rem;
    }

    th {
        color: var(--color-steel);
        font-size: 0.78rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    &__actions {
        display: flex;
        gap: 0.5rem;
    }
}

.sym-editor {
    display: grid;
    grid-template-columns: minmax(320px, 3fr) minmax(200px, 2fr);
    gap: 1.5rem;

    &__subtitle {
        margin: 1rem 0 0.5rem;
        font-size: 0.78rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--color-steel);
    }

    &__preview {
        border: 1px solid var(--color-line);
        border-radius: 8px;
        padding: 0.75rem 1rem;
        align-self: start;

        ::v-deep .legend-entry {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.15rem 0;
            font-size: 0.85rem;
        }

        ::v-deep .legend-icon {
            max-width: 20px;
            max-height: 20px;
        }
    }
}

.sym-palette {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    select {
        flex: 1;
    }

    //"Contraste alto" no dice nada sin verlo.
    &__strip {
        display: flex;

        span {
            width: 14px;
            height: 14px;
        }
    }
}

.sym-class {
    border: 1px solid var(--color-line);
    border-radius: 6px;
    padding: 0.6rem 0.75rem;
    margin-bottom: 0.6rem;

    &__head {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }
}

</style>
